import { discord_guildsId } from "../../types/discord_guilds";
import { discord_membersId, DiscordMembers } from "../../types/discord_members";
import { workspacesId } from "../../types/workspaces";

/**
 * Tag data interface for Discord members
 */
export type MemberTag = {
  id: number;
  name: string;
  color: string;
};

/**
 * Tag data needed from user for adding a new member tag
 * Properties like color and id aren't needed here because they're auto-generated
 */
export type NewMemberTag = {
  name: string;
  description: string;
};

/**
 * Role data interface for Discord members
 */
export type MemberRole = {
  discord_id: string;
  name: string;
};

/**
 * Base member data that will be expected on Discord member tables source data. Inherits from
 * discord_members table def and adds tags and discord_roles attributes.
 */
export type AllMembers = DiscordMembers & {
  tags?: MemberTag[];
  discord_roles?: MemberRole[];
};

/**
 * Member data type for Discord at-risk members table. Inherits from AllMembers and adds at-risk
 * column data (e.g. last_month_count)
 */
export type AtRiskMember = AllMembers & {
  last_month_count: number;
  this_month_count: number;
  total_count: number;
  last_posted: Date;
};

/**
 * Member data type for Discord active members table and custom view tables. Inherits from
 * AllMembers and adds activity column data (e.g. parent_count)
 */
export type DiscordMemberRpc = AllMembers & {
  parent_count: number;
  thread_count: number;
  reaction_count: number;
  total_count: number;
};

/**
 * Data type for retention graph. Matches the data definition returned from discord_retention RPC.
 */
export type RetentionData = {
  cohort_month: string;
  retention_month: number;
  members_joined: number;
  members_active: number;
  // active_ids: string;
  // joined_ids: string;
  retention_rate: number;
};

/**
 * Type for discordQueries/getDiscordActiveMembersOverTime and discordQueries/getDiscordActiveMembersOverTimeByWeek
 * These queries return Discord active members gruped by day or week respectively
 * for_date: day date, when Discord active members are grouped by day
 * weekly: week date, when Discord active members are grouped by week
 * total_count: total Discord active members count
 */
export type ActiveMembersOverTimeRpc = {
  for_date: string;
  total_count: number;
  weekly: string;
};

/**
 * Type for discordQueries/getDiscordMostActiveMembers
 */
export type MostActiveMembersRpc = {
  discord_id: string;
  name: string;
  messages: string;
  avatar: string;
};

/**
 * Type for discordQueries/getDiscordMostActiveChannels
 */
export type MostActiveChannelsRpc = {
  channel_id: string;
  channel_name: string;
  messages: number;
};

/**
 * Type for discord_recent_member_activity RPC, used by discordQueries/getDiscordMemberRecentActivity
 */
export type DiscordRecentMemberActivity = {
  channel_id: string;
  channel_name: string;
  msg_content: string;
  ts: string;
  permalink: string;
  ageSeconds: number;
  ageText: string;
};

/**
 * Type for discord_member_channel_counts RPC, used by discordQueries/getDiscordMemberChannelCounts
 */
export type DiscordMemberChannelCounts = {
  channel_id: string;
  channel_name: string;
  parent_count: number;
  thread_count: number;
  reaction_count: number;
  total_count: number;
};

/**
 * Type for exported members in CSV file
 */
export interface ParsedCSVMember {
  id?: discord_membersId;
  created_at?: Date | null;
  guild?: discord_guildsId;
  discord_id?: string;
  discriminator?: string;
  username?: string;
  updated_at?: Date | null;
  avatar?: string | null;
  is_bot?: boolean;
  joined_at?: Date | null;
  workspace?: workspacesId | null;
  tags?: MemberTag[];
  discord_roles?: MemberRole[];
  key?: string;
}

/**
 * Type for discord_popular_threads RPC, used by discordQueries/getPopularThreads
 */
export type PopularThreadsRpc = {
  id: string;
  name: string;
  channel: string;
  channel_id: string;
  member_count: number;
  thread_count: number;
  created_at: string;
};

/**
 * Type for discord_new_cohort_members RPC,
 * used by discordQueries/getNewCohortMemberIds
 */
export type NewCohortMemberIdsRpc = {
  member_id: discord_membersId;
};

/**
 * Type for discord_active_cohort_members RPC,
 * used by discordQueries/getActiveCohortMemberIds
 */
export type ActiveCohortMemberIdsRpc = {
  cohort_month: string;
  retention_month: number;
  active_ids: string;
};

/**
 * Type for trigger
 * used by discordQueries/getDiscordTriggers
 */
export type DiscordTrigger = {
  id: string;
  workspace: string;
};

/**
 * Type for get_discord_triggers RPC,
 * used by discordQueries/getDiscordTriggers
 */
export type DiscordTriggersRpc = {
  trigger_id: string;
  root_id: string;
  executions: number;
  created_by: string;
  name: string;
  is_active: boolean;
  is_archived: boolean;
};

/**
 * Type for get_trigger_executions RPC,
 * used by discordQueries/getTriggerExecutions
 */
export type TriggerExecutionsRpc = {
  trigger_id: string;
  root_id: string;
  first_step_finished_at: string;
  last_step_id: string;
  last_step_finished_at: string;
  last_step_status: string;
  _reached_goal: boolean;
  variables: unknown;
  user_id: string;
  username: string;
  avatar: string;
};

/**
 * Type for get_executions_for_community RPC
 */
export type CommunityExecutionsRpc = {
  _trigger_id: string;
  trigger_name: string;
  _root_execution_id: string;
  user_id: string;
  _first_step_finished_at: string;
  last_step_id: string;
  _last_step_finished_at: string;
  last_step_status: string;
  reached_goal: boolean;
  _variables: { user: { id: string; username: string } };
  community_id: string;
  _root_trigger_id: string;
};

/**
 * Type for discord_bot_last_n_executions RPC
 */
export type BotExecutionsRpc = {
  id: string;
  updated_at: string;
  username: string;
  root_trigger_id: string;
  trigger_id: string;
  trigger_name: string;
};

export enum TimePeriod {
  Day = "day",
  Week = "week",
  Month = "month",
}

/**
 * Type for discord_new_members RPC,
 * used by discordQueries/getNewMembers
 */
export type NewMembers = {
  date_joined: string;
  count: number;
};

export enum GroupBy {
  Daily = "daily",
  Hourly = "hourly",
}

/**
 * Type for discord_messages_count_average_daily and discord_messages_count_average_hourly RPC,
 * used by discordQueries/getActiveTime
 */
export type ActiveTimeForMessaging = {
  time_period: string;
  avg: number;
};

export enum RESPONSE_AGREGATES_TASK_TYPES {
  QUESTION = "send_message",
  SURVEY = "send_survey",
  MULTIPLE_SELECT = "send_multiple_select",
}

export type TriggerQuestionResponse = {
  created_at: string;
  username: string;
  discriminator: string;
  root_execution_id: string;
  _step_id: string;
  task_type: RESPONSE_AGREGATES_TASK_TYPES;
  answer: string;
  question: string;
};

export type ResponseAggregatesRpc = {
  count: number;
  step_id: string;
  step_responses: number;
  total_executions: number;
} & Omit<
  TriggerQuestionResponse,
  "created_at" | "username" | "discriminator" | "root_execution_id" | "_step_id"
>;

// antd table column
export type QuestionColumn = {
  title: string | React.ReactNode;
  dataIndex: string;
  key: string;
  createdAt: string;
  rowKey: number;
  stepId: string;
  sorter?: (a: any, b: any) => number;
  filters?: any[];
  onFilter?: (value: string, record: any) => boolean;
  sortDirections?: string[];
  filteredValue?: any;
  fixed?: "left" | "right";
  width?: number;
  render?: (text: any, record: any, index: number) => React.ReactNode;
};
